import { AfterViewInit, Component, computed, effect, inject, output, TemplateRef, viewChild } from '@angular/core';

import { _CentralPageService } from '../../services/_central-page.service';
import { _TabPageService } from '../../services/_tab-page.service';
import { _SectionBaseComponent } from 'src/app/infrastructure/components/section-components/_section-base-component';
import { sectionLayout } from 'src/app/infrastructure/models/section-layout';
import { showAnimation } from 'src/app/infrastructure/animations/show-animation';
import { CommonModule, NgTemplateOutlet } from '@angular/common';
import { pageTransitionAnimation } from 'src/app/infrastructure/animations/route-animations';
import { ActivatedRouteService } from 'src/app/infrastructure/services/route-service/activated-route.service';
import { ApplicationUserService } from 'src/app/infrastructure/services/application-user.services/application-user.service';
import { slideAnimation } from 'src/app/infrastructure/animations/slide-animation';
import { centralPageAnimation } from 'src/app/infrastructure/animations/central-page-animations';
import { ButtonBlockDetailsSectionComponent } from '../../components/button-blocks/button-block-details-section/button-block-details-section.component';
import { ButtonsBlockEditSectionComponent } from '../../components/button-blocks/button-block-edit-section/button-block-edit-section.component';
import { _FormModelService } from 'src/app/infrastructure/services/model-services/_form-model.service';
import { pageMode } from 'src/app/infrastructure/models/page-mode';
import { PageSectionName } from 'src/app/infrastructure/services/page-services/models/page-section-name';
import { LogService } from 'src/app/infrastructure/services/log.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { AnimationService } from 'src/app/infrastructure/services/loading-services/animation.service';
import { motionDuration_read } from 'src/app/infrastructure/animations/motion-duration';

@Component({
    selector: 'app-section-details',
    templateUrl: './section-details.component.html',
    styleUrls: [
        '../../central-page.scss',
        './section-details.component.scss'
    ],
    host: {
        'class': 'details-sheet'
    },
    standalone: true,
    imports: [
        ButtonBlockDetailsSectionComponent,
        ButtonsBlockEditSectionComponent,
        NgTemplateOutlet,
        CommonModule,
    ],
    animations: [
        centralPageAnimation,
        pageTransitionAnimation,
        showAnimation,
        slideAnimation,
    ]
})
export class SectionDetailsComponent
    implements AfterViewInit
{
    protected readonly _activatedRouteService = inject(ActivatedRouteService, {optional: true});
    protected readonly _centralPageService = injectRequired(_CentralPageService)
    protected readonly _formModelService = inject(_FormModelService, {optional: true});
    protected readonly _applicationUserService = injectRequired(ApplicationUserService);

    private readonly __animationService = injectRequired(AnimationService);
    private readonly __tabPageService = inject(_TabPageService, {optional: true});
    private __template_sectionDetails_buttons$$ = viewChild<TemplateRef<any>>('sectionDetails_buttons');
    private __template_sectionEdit_buttons$$ = viewChild<TemplateRef<any>>('sectionEdit_buttons');

    // Paginawissel-duur = gedeelde animatieduur van het thema (--layout-motion-duration, zie motion-duration.ts),
    // gelezen bij elke standwissel zodat een thema-wijziging direct meetelt.
    protected _motionDuration$$ = computed(() => { this._centralPageService.pageMode$$(); return motionDuration_read(); });
    protected _showHideEnterTime$$ = this._motionDuration$$;
    protected _showHideLeaveTime$$ = this._motionDuration$$;

    protected _isContentLayout$$ = computed(() =>
        (this.__tabPageService?.sectionLayout$$() ?? sectionLayout.form) === sectionLayout.content
    );
    // Content-layout (bv. Theme): details direct tonen bij verbergen/tonen van de tab — maar terug uit edit/create
    // wél infaden, zelfde cross-fade als details -> edit.
    protected _detailsEnterTime$$ = computed(() => {
        const __prev = this._centralPageService.pageMode_prev$$();
        const __fromEdit = __prev === pageMode.editPage || __prev === pageMode.createPage;
        return this._isContentLayout$$() && !__fromEdit ? '0ms' : this._showHideEnterTime$$();
    });
    
    out_btnCancel = output({alias: 'btnCancel'});
    out_btnDelete = output({alias: 'btnDelete'});
    out_btnEdit = output({alias: 'btnEdit'});
    out_btnSave = output({alias: 'btnSave'});

    sectionDetailsShow$$ = computed(() => {
        const _pageMode = this._centralPageService.pageMode$$();
        return _pageMode == pageMode.detailsPage || _pageMode == pageMode.mediumPage;
    });
    sectionDetails_extraButtons$$ = this._centralPageService.sectionDetails_extraButtons_template$$;
    sectionDetails_template$$ = this._centralPageService.template_sectionDetails$$;

    sectionEditShow$$ = computed(() => {
        const _pageMode = this._centralPageService.pageMode$$();
        return _pageMode == pageMode.editPage || _pageMode == pageMode.createPage;
    });
    sectionEdit_extraButtons$$ = this._centralPageService.sectionEdit_extraButtons_template$$;
    // Details → Edit: Edit wordt onzichtbaar opgebouwd tot hij klaar meldt (model + startrequests); Details blijft zolang
    // staan. Daarna cross-fade (Details @showHide leave, Edit .edit-page--reveal), zodat Edit in één keer verschijnt.
    editPending$$ = computed(() => this.sectionEditShow$$() && !this._centralPageService.sectionEditReady$$());
    detailsVisible$$ = computed(() => this.sectionDetailsShow$$() || this.editPending$$());
    // Edit kwam via de wacht (vanuit Details): eigen fade bij het tonen, @showHide-enter dan direct (liep onzichtbaar).
    editGated$$ = computed(() => {
        const __prev = this._centralPageService.pageMode_prev$$();
        return this._centralPageService.pageMode$$() == pageMode.editPage
            && (__prev == pageMode.detailsPage || __prev == pageMode.mediumPage);
    });
    protected _editEnterTime$$ = computed(() => this.editGated$$() ? '0ms' : this._showHideEnterTime$$());
    sectionEdit_template$$ = this._centralPageService.template_sectionEdit$$;

    constructor() {
        effect(() => {
            this._centralPageService.sectionDetails_buttons_template_set(this.__template_sectionDetails_buttons$$());
            this._centralPageService.sectionEdit_buttons_template_set(this.__template_sectionEdit_buttons$$());
        });
    }

    // De details↔edit cross-fade (@showHide) loopt buiten @slidePageSheetWidth (sheetWidth blijft
    // small→small). Voed daarom de animationService zelf, zodat animating$$ — en dus de scroll-gating
    // in tab-page (scrollReady$$) — de cross-fade volgt en .details-page/.edit-page niet kort stapelen.
    emit_animation_start(): void {
        this.__animationService.add();
    }

    emit_animation_end(): void {
        this.__animationService.remove();
    }

    emit_btnCancel(): void {
        this._formModelService?.form_resetFromDirty();
        this._centralPageService.onCancel_emit();

        this.out_btnCancel.emit();
    }

    emit_btnDelete(): void {
        this._centralPageService.onDelete_emit();
        this.out_btnDelete.emit();
    }

    emit_btnEdit(): void {
        this._centralPageService.pageMode_set(pageMode.editPage);
        this.out_btnEdit.emit();
    }

    emit_btnSave(): void {
        this._centralPageService.onSave_emit();
    }

    ngAfterViewInit(): void {
        if(!this.sectionDetails_template$$() && !this.sectionEdit_template$$()) {
            LogService.envDev.error('templates details section missing!!');
        }
    }
}
